import React, { FC, useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { ChevronRight, Copy, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import styles from './duplicate_tasks.module.css';
import BulkActionsBar from '../kanban/bulk_actions_bar';
import Avatar, { Member } from '../../../../clients/shared/Avatar';
import cx from 'classnames';
import {
  NewStateUpdateBulkRequestArgs,
  UpdateState
} from 'appJS/models/BulkRequest';
import track from 'appJS/utils/analytics';

type DuplicateTaskAssignee = {
  name: string;
  email: string;
  avatar: string;
  first_name?: string;
  surname?: string;
  role?: Member['role'];
};

type DuplicateTask = {
  id: number;
  local_task_id: number;
  title?: string;
  description?: string;
  status?: number;
  priority_id?: number;
  created_at?: string;
  requester_display?: string;
  status_label?: string;
  assignees?: DuplicateTaskAssignee[];
};

interface DuplicateTasksProps {
  duplicateTasks: DuplicateTask[];
  container: () => HTMLElement;
  projectId: number;
  destinations: Array<{
    id: string | number;
    name: string;
    status_id: number | null;
  }>;
  currentTask?: DuplicateTask;
  isUpdatingTasks: boolean;
  onBulkUpdateStart: (
    payload: Pick<
      NewStateUpdateBulkRequestArgs,
      'taskIds' | 'targetState' | 'source'
    >
  ) => void;
  onBulkUpdateSuccess: () => Promise<void>;
  onBulkUpdateError: () => void;
  setActiveTask: (taskId: number) => void;
}

const priorityClasses = [
  'notSet',
  'critical',
  'important',
  'normal',
  'minor'
] as const;

const formatReportedMeta = (createdAt?: string, requesterDisplay?: string) => {
  if (!createdAt || !requesterDisplay) {
    return null;
  }

  const parsedDate = new Date(createdAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return `Reported ${format(parsedDate, 'MMM d yyyy')}, by ${requesterDisplay}`;
};

const getPriorityClassName = (priorityId?: number) =>
  priorityClasses[priorityId ?? 0] || priorityClasses[0];

const formatStatusLabel = (statusLabel?: string) => {
  if (!statusLabel) {
    return null;
  }

  return statusLabel.replace(/\s+/g, '').toUpperCase();
};

export const getPendingTaskStatusLabels = ({
  taskIds,
  destinations,
  targetState
}: {
  taskIds: number[];
  destinations: Array<{
    id: string | number;
    name: string;
    status_id: number | null;
  }>;
  targetState: UpdateState;
}) => {
  const columnDestination =
    typeof targetState.columnId === 'number'
      ? destinations.find(
          destination => Number(destination.id) === targetState.columnId
        )
      : undefined;

  const statusDestination =
    typeof targetState.statusId === 'number'
      ? destinations.find(
          destination => destination.status_id === targetState.statusId
        )
      : undefined;

  const pendingStatusLabel = columnDestination?.name || statusDestination?.name;

  if (!pendingStatusLabel) {
    return {};
  }

  return taskIds.reduce<Record<number, string>>((labels, taskId) => {
    labels[taskId] = pendingStatusLabel;
    return labels;
  }, {});
};

export const getTaskBadgeLabel = ({
  task,
  statusLabels,
  pendingStatusLabel
}: {
  task: DuplicateTask;
  statusLabels: Record<number, string>;
  pendingStatusLabel?: string;
}) => {
  const statusLabel =
    pendingStatusLabel ||
    task.status_label ||
    (typeof task.status === 'number' ? statusLabels[task.status] : null);

  return statusLabel
    ? `Task #${task.local_task_id} | ${formatStatusLabel(statusLabel)}`
    : `Task #${task.local_task_id}`;
};

const DuplicateTasks: FC<DuplicateTasksProps> = ({
  duplicateTasks,
  container,
  projectId,
  destinations,
  currentTask,
  isUpdatingTasks,
  onBulkUpdateStart,
  onBulkUpdateSuccess,
  onBulkUpdateError,
  setActiveTask
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [pendingTaskStatusLabels, setPendingTaskStatusLabels] = useState<
    Record<number, string>
  >({});
  const trackedRecommendationTaskId = useRef<number | null>(null);

  const title = `Similar task${duplicateTasks.length > 1 ? 's' : ''} found`;
  const statusLabels = destinations.reduce((labels, destination) => {
    if (destination.status_id === null) {
      return labels;
    }

    labels[destination.status_id] = destination.name;
    return labels;
  }, {});

  const visibleTasks = !currentTask
    ? duplicateTasks
    : [
        currentTask,
        ...duplicateTasks.filter(task => task.id !== currentTask.id)
      ];

  const allSelected =
    visibleTasks.length > 0 && selectedTaskIds.length === visibleTasks.length;
  const currentTaskId = currentTask?.id ?? null;

  useEffect(() => {
    if (duplicateTasks.length === 0 || currentTaskId === null) {
      trackedRecommendationTaskId.current = null;
      return;
    }

    if (trackedRecommendationTaskId.current === currentTaskId) {
      return;
    }

    track('Similar task recommendation shown', {
      task_id: currentTaskId,
      count: duplicateTasks.length
    });
    trackedRecommendationTaskId.current = currentTaskId;
  }, [currentTaskId, duplicateTasks.length]);

  if (duplicateTasks.length === 0) {
    return null;
  }

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedTaskIds([]);
    setPendingTaskStatusLabels({});
  };

  const handleOpenModal = () => {
    if (isModalOpen) {
      return;
    }

    track('Viewed similar tasks', {
      count: visibleTasks.length
    });
    setIsModalOpen(true);
  };

  const toggleTaskSelection = (taskId: number) => {
    setSelectedTaskIds(currentTaskIds =>
      currentTaskIds.includes(taskId)
        ? currentTaskIds.filter(id => id !== taskId)
        : [...currentTaskIds, taskId]
    );
  };

  const toggleAllTasks = () => {
    if (allSelected) {
      setSelectedTaskIds([]);
      return;
    }

    setSelectedTaskIds(visibleTasks.map(task => task.id));
  };

  return (
    <div className={styles.duplicateTasksWrapper}>
      <div className={styles.duplicateTasksContent}>
        <button
          type="button"
          className={styles.duplicateTasksTitle}
          onClick={handleOpenModal}
          data-cy="duplicateTasksTrigger"
        >
          <div className={styles.titleLeft}>
            <span className={styles.iconWrapper}>
              <Copy size={10} />
            </span>
            {title}
            <ChevronRight size={14} />
          </div>
        </button>
        <Modal
          open={isModalOpen}
          onCancel={handleClose}
          title={
            <div className={styles.modalTitle}>
              <span className={styles.iconWrapper}>
                <Copy size={10} />
              </span>
              {title}
            </div>
          }
          footer={null}
          getContainer={container}
          destroyOnClose
          className={styles.modal}
          data-cy="duplicateTasksModal"
          width={600}
        >
          <div className={styles.modalBody}>
            <hr className={styles.modalDivider} />
            <label className={styles.selectAllRow}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAllTasks}
                aria-label="Select all similar tasks"
                disabled={isUpdatingTasks}
              />
              <span className={styles.selectAllLabel}>Select all</span>
            </label>
            <div className={styles.selectAllRowContainer}>
              {visibleTasks.map(task => {
                const isSelected = selectedTaskIds.includes(task.id);
                const priorityClassName =
                  styles[getPriorityClassName(task.priority_id)];
                const taskBadgeLabel = getTaskBadgeLabel({
                  task,
                  statusLabels,
                  pendingStatusLabel: pendingTaskStatusLabels[task.id]
                });
                const reportedMeta = formatReportedMeta(
                  task.created_at,
                  task.requester_display
                );

                return (
                  <div
                    key={task.local_task_id}
                    className={styles.taskRow}
                    data-testid={`duplicate-task-row-${task.id}`}
                  >
                    <label className={styles.taskCheckbox}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTaskSelection(task.id)}
                        data-cy={`duplicateTaskCheckbox-${task.id}`}
                        disabled={isUpdatingTasks}
                      />
                    </label>
                    <div
                      className={cx(styles.taskItem, {
                        [styles.taskItemLoading]: isUpdatingTasks
                      })}
                      data-cy={`duplicateTaskRow-${task.id}`}
                      data-testid={`duplicate-task-card-${task.id}`}
                      aria-busy={isUpdatingTasks}
                    >
                      {isUpdatingTasks && (
                        <div
                          className={styles.taskItemLoadingOverlay}
                          data-testid={`duplicate-task-loading-${task.id}`}
                        >
                          <Spin size="small" />
                          <span className={styles.taskItemLoadingText}>
                            Updating task
                          </span>
                        </div>
                      )}
                      <div
                        className={`${styles.priorityIndicator} ${priorityClassName}`}
                        data-testid={`duplicate-task-priority-indicator-${task.id}`}
                      />
                      <div className={styles.taskItemHeader}>
                        <div
                          className={`${styles.taskBadge} ${priorityClassName}`}
                        >
                          {taskBadgeLabel}
                        </div>

                        <a
                          href={`/projects/${projectId}/tasks/${task.local_task_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.taskItemActionButton}
                          aria-label={`View task ${task.local_task_id}`}
                        >
                          <span>View task</span>
                          <ExternalLink size={14} />
                        </a>
                      </div>
                      <div className={styles.taskItemContent}>
                        <span
                          className={cx(styles.taskText, {
                            [styles.taskTextWithAssignees]: !!task.assignees
                              ?.length
                          })}
                        >
                          {task.title || task.description}
                        </span>

                        <span
                          className={cx(styles.taskMeta, {
                            [styles.taskMetaNoAssignees]: !task.assignees
                              ?.length
                          })}
                        >
                          {reportedMeta && (
                            <span className={styles.taskReporter}>
                              {reportedMeta}
                            </span>
                          )}

                          {!!task.assignees?.length && (
                            <div className={styles.assigneesContainer}>
                              {task.assignees
                                .slice(0, 3)
                                .reverse()
                                .map(assignee => (
                                  <div
                                    className={styles.avatarContainer}
                                    key={assignee.email}
                                    title={assignee.email}
                                  >
                                    <Avatar member={assignee} size="small" />
                                  </div>
                                ))}
                            </div>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <hr className={styles.modalDivider} style={{ margin: '12px 0' }} />
            <BulkActionsBar
              inline
              show
              closable={false}
              clearable={false}
              showSelectedCount={false}
              setSelectedTasks={() => {}}
              selectedTasks={selectedTaskIds.map(taskId => taskId.toString())}
              onClose={() => {}}
              onSuccess={action => {
                if (action === 'tasks_merged') {
                  handleClose();
                }
              }}
              bulkActions={[]}
              setActiveTask={setActiveTask}
              permissions={{
                editTaskStatus: true,
                editTaskAssignees: true,
                billingRights: true,
                taskMerge: true
              }}
              order={['taskMerge', 'editTaskAssignees', 'editTaskStatus']}
              containerClassName={styles.bulkActionsBar}
              innerContainerClassName={styles.bulkActionsBarInnerContainer}
              bulkRequestSource="similar_tasks_modal"
              onBulkUpdateStart={payload => {
                const labels = getPendingTaskStatusLabels({
                  taskIds: payload.taskIds,
                  destinations,
                  targetState: payload.targetState
                });

                if (Object.keys(labels).length > 0) {
                  setPendingTaskStatusLabels(currentLabels => ({
                    ...currentLabels,
                    ...labels
                  }));
                }

                onBulkUpdateStart(payload);
              }}
              onBulkUpdateSuccess={onBulkUpdateSuccess}
              onBulkUpdateError={() => {
                setPendingTaskStatusLabels({});
                onBulkUpdateError();
              }}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default DuplicateTasks;
